import { gql } from "@apollo/client";
import { produce } from "immer";
import { of } from "rxjs";
import "../../api/types.js";
import { UserAccountStatus, FeatureAccessStatus } from "../../contract/pam.js";
import "moment-timezone";
import "react-intl";
import "@apollo/client/errors";
import "rxjs/operators";
import { EntityBaseGqlService } from "../../api/EntityBaseGqlService.js";
import "graphql";
import "@opentelemetry/api";
import "../../otel/TraceProvider.js";
import "react";
import "react-error-boundary";
import { map } from "../../otel/rxjs.js";
import "@opentelemetry/context-zone";
import "@opentelemetry/exporter-trace-otlp-http";
import "@opentelemetry/instrumentation";
import "@opentelemetry/instrumentation-document-load";
import "@opentelemetry/instrumentation-xml-http-request";
import "@opentelemetry/resources";
import "@opentelemetry/sdk-trace-web";
import "@opentelemetry/semantic-conventions";
import "@apollo/client/link/subscriptions";
import "@apollo/client/utilities";
import "cross-fetch";
import "graphql-ws";
import "../../node_modules/moment/dist/moment.js";
import "rxjs/ajax";
import { userProfileMappingKeys } from "./mapper.js";
import { UserProfileRequestBuilder } from "./requestBuilder.js";
class UserProfileService extends EntityBaseGqlService {
  _mapper;
  _requestBuilder;
  constructor(client, messageLocalizer, mapper) {
    const builder = new UserProfileRequestBuilder();
    super(client, builder, messageLocalizer);
    this._mapper = mapper;
    this._requestBuilder = builder;
  }
  updateNonWdBalance(payload) {
    return this._service.mutate(this._requestBuilder.getUpdateNonWDBalanceMutation(), payload).pipe(map((r) => ({ ...r, responsePayload: r.responsePayload?.updateNonWithdrawableBalance, requestPayload: payload })));
  }
  getErrorSeverity(code) {
    const errorMapping = {
      "20000070": 1,
      "20000071": 1,
      "20000063": 1,
      "20000064": 1,
      KEYCLOAK_CONNECTION_EXISTS: 1,
      PLAYER_NOT_FOUND: 1,
      EXCEED_TOTAL_BALANCE: 1,
      CAN_NOT_BE_NEGATIVE_NUMBER: 1
    };
    return errorMapping[code] ?? 0;
  }
  get(requestPayload) {
    return this.fetchData(requestPayload).pipe(
      map(({ responsePayload, ...response }) => {
        const items = responsePayload?.getUserProfiles?.items?.map((i) => {
          return produce(i, (user) => {
            if (responsePayload?.getPlayerReferralLink) {
              user.referral = user.referral ?? {};
              user.referral.personal_referral_link = responsePayload.getPlayerReferralLink;
            }
            const requestFields = requestPayload?.fields;
            if (requestFields?.includes("account_status")) {
              user.account_status = user.account_status ?? UserAccountStatus.Active;
            }
            if (requestFields.includes("deposit_status")) {
              user.deposit_status = user.deposit_status ?? FeatureAccessStatus.Unlocked;
            }
            if (requestFields.includes("p2p_transfer_status")) {
              user.p2p_transfer_status = user.p2p_transfer_status ?? FeatureAccessStatus.Unlocked;
            }
            if (requestFields.includes("withdrawal_status")) {
              user.withdrawal_status = user.withdrawal_status ?? FeatureAccessStatus.Unlocked;
            }
            if (requestFields.includes("lobby_access_status")) {
              user.lobby_access_status = user.lobby_access_status ?? FeatureAccessStatus.Unlocked;
            }
            if (requestFields.includes("agent_info") || requestFields.includes("agent_info.default_agent_revenue_share_history")) {
              user.agent_info = {
                ...user.agent_info ?? {},
                agent_revenue_share_list: this._mapper.map(
                  user?.agent_info?.default_agent_revenue_share_history,
                  userProfileMappingKeys.agentRevenueShareViewModelListSerialized,
                  userProfileMappingKeys.agentRevenueShareList
                )
              };
            }
            const masterAgent = responsePayload?.getMasterAgent;
            if (masterAgent) {
              user.master_agent_id = masterAgent.uid;
              user.agent_info = {
                ...user?.agent_info ?? {},
                agent_revenue_share_list: user?.agent_info?.agent_revenue_share_list ?? [{}]
              };
              user.agent_info.agent_revenue_share_list[user.agent_info.agent_revenue_share_list.length - 1].commission_subtypes = masterAgent.commission_subtypes;
            }
            return user;
          });
        }) ?? [];
        return {
          ...response,
          requestPayload,
          responsePayload: {
            items,
            total: items.length ? responsePayload?.getUserProfiles?.total_count ?? 0 : 0
          }
        };
      })
    );
  }
  updateAffiliateBtag(user) {
    return this._service.mutate(this._requestBuilder.buildUpdateAffiliateBtagMutation(), {
      uid: user.uid,
      affiliate: { btag: user.affiliate.btag }
    }).pipe(map((r) => ({ ...r, requestPayload: user, responsePayload: r.responsePayload.updateUser })));
  }
  getDownloadLink(requestPayload) {
    const request = this._requestBuilder.buildDownloadRequest(requestPayload);
    return this._service.query(request.query, request.variables).pipe(
      map((response) => ({
        ...response,
        requestPayload,
        responsePayload: response?.responsePayload?.exportUserProfiles?.url
      }))
    );
  }
  updateAgentInfo(user) {
    return user?.uid ? this._service.mutate(this.getUpdateAgentInfoMutation(), {
      uid: user.uid,
      user
    }).pipe(map((r) => ({ ...r, responsePayload: r.responsePayload.updateUser }))) : of();
  }
  getUpdateAgentInfoMutation() {
    return gql`
            mutation UpdateUser($uid: String!, $user: UserProfileInput!) {
                updateUser(uid: $uid, user: $user) {
                    uid
                    agent_info {
                        bo_agent_id
                        agent_granted_ts {
                            seconds
                        }
                        default_agent_revenue_share_history
                    }
                }
            }
        `;
  }
  updateAgentRevenueShare(payload) {
    const { agentId, agentRevenueShare } = payload;
    return this._service.mutate(this.getUpdateRevenueShareMutation(), {
      agent_id: agentId,
      revenue_share: {
        casino_share: agentRevenueShare?.casino_share,
        commission_subtypes: agentRevenueShare?.commission_subtypes,
        poker_cash: agentRevenueShare?.poker_cash,
        poker_cash_plo: agentRevenueShare?.poker_cash_plo,
        poker_mtt: agentRevenueShare?.poker_mtt,
        spins: agentRevenueShare?.spins,
        sports: agentRevenueShare?.sports
      }
    }).pipe(map((r) => ({ ...r, requestPayload: payload, responsePayload: r.responsePayload.updateRevenueShare })));
  }
  getUpdateRevenueShareMutation() {
    return gql`
            mutation UpdateRevenueShare($agent_id: String!, $revenue_share: AgentRevenueShareInput!) {
                updateRevenueShare(agent_id: $agent_id, revenue_share: $revenue_share)
            }
        `;
  }
}
export {
  UserProfileService
};
//# sourceMappingURL=service.js.map
